/**
 * Sign-in hints from the typed address, with no lookup, so nothing anonymous can probe federation.
 * The B2C custom policy ignores a `domain_hint` no claims provider matches; that is expected,
 * not a bug.
 */

import type { SiteSignInOptions } from './site-sign-in-options'

/** The two hints handed to `loginRedirect`. */
export interface SignInHints {
  /** The full address, trimmed. */
  readonly loginHint: string
  /** Everything after the last `@`, lowercased. */
  readonly domainHint: string
}

/** What `loginRedirect` is given: the hints, and the authority when the site signs in through its own policy. */
export interface SignInRequest extends SignInHints {
  /** The configured authority with the site's custom policy in place of its user flow, or none to keep it. */
  readonly authority?: string
  /** B2C's `id_token_hint`, for a one-time sign-in link (SM-39). */
  readonly extraQueryParameters?: Readonly<Record<string, string>>
}

/** Derives the sign-in hints. `domainHint` is empty when the address has no domain part. */
export function deriveSignInHints(email: string): SignInHints {
  const loginHint = email.trim()

  // lastIndexOf: a quoted local part may contain `@`; the domain follows the final one.
  const separator = loginHint.lastIndexOf('@')
  const domainHint =
    separator >= 0 && separator < loginHint.length - 1
      ? loginHint.slice(separator + 1).toLowerCase()
      : ''

  return { loginHint, domainHint }
}

/**
 * The sign-in request for the typed address on the current site: the site's domain hint when it
 * has one; otherwise its custom policy, when it has one, as the authority's last segment.
 */
export function buildSignInRequest(
  email: string,
  site: SiteSignInOptions | null,
  authority: string,
): SignInRequest {
  const hints = deriveSignInHints(email)

  // A sign-in link goes through its own policy with its hint alone: nothing typed, no domain hint.
  const idTokenHint = site?.idTokenHint?.trim() ?? ''
  const linkPolicy = site?.customB2CPolicy?.trim() ?? ''
  if (idTokenHint.length > 0 && linkPolicy.length > 0) {
    return { loginHint: '', domainHint: '', authority: withPolicy(authority, linkPolicy), extraQueryParameters: { id_token_hint: idTokenHint } }
  }

  const domainHint = site?.domainHint?.trim() ?? ''
  if (domainHint.length > 0) {
    return { ...hints, domainHint }
  }

  const policy = site?.customB2CPolicy?.trim() ?? ''
  return policy.length > 0 ? { ...hints, authority: withPolicy(authority, policy) } : hints
}

/** `https://{tenant}.b2clogin.com/{tenant}.onmicrosoft.com/{flow}` with `policy` in place of `{flow}`. */
function withPolicy(authority: string, policy: string): string {
  const url = new URL(authority)
  const segments = url.pathname.split('/').filter((segment) => segment.length > 0)
  segments[segments.length - 1] = encodeURIComponent(policy)
  url.pathname = `/${segments.join('/')}`
  return url.toString()
}
